/**
 * The tags a page puts in <head>, in one place.
 * @file src/web/shared/hooks/useSEO.ts
 *
 * ── Why a hook and not a library ──────────────────────────────────────────
 * This is a single-page app, so there is one <head> and every route rewrites
 * it. The native document API does that in twenty lines; react-helmet does it
 * in a dependency. What matters is not the mechanism but that the mechanism
 * is used ONCE — a page that sets document.title by hand gets none of the
 * canonical, Open Graph or Twitter tags below, and nobody notices until a
 * link posted in Slack unfurls as a bare URL.
 *
 * ── What every page gets without asking ───────────────────────────────────
 * A brand-suffixed title, a self-referencing canonical, a full Open Graph and
 * Twitter card, and the site name. Defaults used to be the scaffold's own
 * ("Feature-Based Component Architecture with UIKit components"), which is
 * what shipped on every page that did not override them.
 *
 * ── The canonical is absolute, and points at production ───────────────────
 * Not `window.location.href`. A preview deployment, a staging host, or the
 * same page reached with ?utm_source= on it are all the same document, and a
 * canonical built from the current URL cheerfully tells a crawler they are
 * four. So: the production origin plus the route's own path, nothing else.
 *
 * ── Social scrapers do not run this ───────────────────────────────────────
 * Facebook, LinkedIn, Slack and WhatsApp read the served HTML and stop. They
 * never see what this hook writes, which is why index.html carries a complete
 * default card of its own. This hook is for the crawlers that do render —
 * Google chief among them — and for the browser tab.
 *
 * @llm-rule WHEN: Any routed page — always via <SEO>, never document.title
 * @llm-rule AVOID: Building the canonical from window.location — see above
 * @llm-rule NOTE: Changing the defaults here changes every page that does not
 *   pass its own, so keep them true of the product rather than of one page
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { brand } from '../brand';

/** Where this product actually lives. The canonical's other half. */
export const SITE_ORIGIN = 'https://naumene.com';

/** The social card. Absolute, because a scraper has no page to resolve against. */
export const SITE_CARD = `${SITE_ORIGIN}/og.png`;

export interface SEOOptions {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  /** Overrides the self-referencing default. Pass an absolute URL. */
  canonical?: string;
  /**
   * Keep this page out of the index. For anything behind a login and for the
   * links sent to one client — an intake form, a signing page, a booking
   * page — which are private by intent and must never surface in a result.
   */
  noindex?: boolean;
  /** Structured data for this page. Removed again when the page unmounts. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * The brand goes on the end, once.
 *
 * Pages pass what the page is about ("Pricing"), not what the site is called,
 * so the suffix is added here — except where the title already carries the
 * brand, which the comparison pages do ("Naumene vs Clio Grow"). Repeating
 * it would spend the ~60 characters a result actually shows on saying the
 * same word twice.
 */
function withBrand(title: string): string {
  return title.includes(brand.name) ? title : `${title} | ${brand.name}`;
}

/** Set or update one meta tag, keyed by name= or property=. */
function setMeta(key: string, content: string, asProperty = false) {
  const attr = asProperty ? 'property' : 'name';
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Remove one, for the tags that are conditional. */
function dropMeta(key: string, asProperty = false) {
  const attr = asProperty ? 'property' : 'name';
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

export const useSEO = ({
  title = `Client intake software for law firms | ${brand.name}`,
  description = 'Client intake that tells you which enquiries you are losing, and why. $29 per user, per month, alongside the case system you already have.',
  /* Empty on purpose. No search engine has ranked on the keywords tag in over
   * a decade; the scaffold's "react, fbca, uikit, typescript" was being served
   * on every page that did not override it. Kept as a prop only so the few
   * pages that want one are not forced to reach around the hook. */
  keywords = '',
  ogTitle,
  ogDescription,
  ogImage = SITE_CARD,
  canonical,
  noindex = false,
  jsonLd,
}: SEOOptions = {}) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const full = withBrand(title);

    /* One path, one URL: no trailing slash except at the root, so /pricing
     * and /pricing/ cannot canonicalise to two different documents. */
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const url = canonical ?? `${SITE_ORIGIN}${path}`;

    document.title = full;

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    else dropMeta('keywords');

    if (noindex) setMeta('robots', 'noindex, nofollow');
    else dropMeta('robots');

    setMeta('og:title', ogTitle || full, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:url', url, true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', brand.name, true);
    setMeta('og:locale', 'en_US', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || full);
    setMeta('twitter:description', ogDescription || description);
    setMeta('twitter:image', ogImage);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogImage,
    canonical,
    noindex,
    pathname,
  ]);

  /*
   * Structured data, in its own effect and removed on the way out.
   *
   * Separate because it is the one tag that must NOT accumulate: a script
   * left behind by the previous route would have the homepage's product
   * markup describing the privacy policy. Serialised into the dependency so
   * an object rebuilt on every render does not reinsert it on every render.
   */
  const serialised = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    if (!serialised) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.textContent = serialised;
    document.head.appendChild(el);
    return () => el.remove();
  }, [serialised]);
};
